/**
 * @fileoverview Dropdown widget — single-select with per-option PASS/FAIL.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <select> driven by `extras.options[]` or a lookup resolver (the
 *   lookup resolver wiring lands in Phase 4 when lookupService is injected).
 * - Drive PASS/FAIL pill from the selected option's `passFail` flag plus
 *   `getFailingMappings` for violation triggering.
 *
 * @component DropdownField
 */
import type { FieldOption } from '@firesquirrel/core-domain';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { useResolveContext } from '../contracts/rendererContext';
import { getPassFailStatus } from '../violations/passFailState';

interface OptionWithPassFail extends FieldOption {
  passFail?: 'pass' | 'fail' | 'neutral';
}

export function DropdownField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as {
    options?: OptionWithPassFail[];
    placeholder?: string;
    lookupType?: string;
    lookupScope?: 'department' | 'system';
  };
  const ctx = useResolveContext();
  // Engine v2's `setOptions` action writes into `resolved.options`; prefer it
  // when the rule has overridden the static set; otherwise use the extras
  // options; finally fall back to the host-injected lookup resolver.
  const lookupOptions = (ctx && extras.lookupType)
    ? ctx.resolveLookup(extras.lookupType, extras.lookupScope) as OptionWithPassFail[]
    : null;
  const options = (resolved?.options as OptionWithPassFail[] | undefined)
    ?? extras.options
    ?? lookupOptions
    ?? [];

  // Pass/fail is owned by `extras.hasPassFail` + `extras.failValues` — see
  // `passFailState.ts`. Violation code bindings are metadata for citation,
  // not the source of truth for which option means FAIL.
  const passFailStatus = getPassFailStatus(field, value, isNA);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="dropdown"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      passFailStatus={passFailStatus}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <select
        id={field.id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={!!errorMessage}
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm outline-hidden focus:ring-2 focus:ring-orange-300 disabled:opacity-50"
      >
        <option value="">{extras.placeholder ?? '— Select —'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} data-passfail={o.passFail ?? 'neutral'}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}
